import {
  DeletePipelineStepsBehavior,
  addOrReplaceAndRunAnalysisStep,
  deleteAnalysisSteps,
  updateAndRunAnalysisStep,
} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {DataReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {
  DataFrameRetypeColumnOptions,
  RetypeColumnType,
} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {withLoadingScreen} from 'util/Promises';

const LOADING_MESSAGE = 'Converting column formats';

async function updateRetypeColumnStep(
  analysis: AnalysisGraphQL,
  retypeColumnStep: GetAnalysisPipelineStep,
  systemName: string,
  newColumnType?: RetypeColumnType
): Promise<void> {
  const previousRetypeColumns = retypeColumnStep.config?.variables.find(
    (variable) => variable.name === 'retypeColumns'
  )?.value as Array<{column: string; type: RetypeColumnType}> | undefined;
  const columnsConfigToKeep = previousRetypeColumns
    ? previousRetypeColumns.flat().filter(({column}) => column !== systemName)
    : [];
  const retypeColumns =
    newColumnType != null
      ? [...columnsConfigToKeep, {column: systemName, type: newColumnType}]
      : columnsConfigToKeep;
  if (retypeColumns.length === 0) {
    await deleteAnalysisSteps(analysis.id, {
      stepIds: [retypeColumnStep.id],
      behavior: DeletePipelineStepsBehavior.NO_INFERENCE,
    });
  } else {
    await updateAndRunAnalysisStep(analysis.id, {
      stepId: retypeColumnStep.id,
      stepConfig: {retypeColumns},
    });
  }
}

async function addRetypeColumnStep(
  analysis: AnalysisGraphQL,
  columnSystemName: string,
  newColumnType: RetypeColumnType,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>
): Promise<void> {
  await addOrReplaceAndRunAnalysisStep(analysis.id, {
    stepType: PipelineStepType.RETYPE_COLUMNS,
    stepVersion: 0,
    stepConfig: {retypeColumns: [{column: columnSystemName, type: newColumnType}]},
    stepInputParents: {[SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference]},
    ...(inputDataFrameId == null ? {} : {stepInput: {inputDataFrameId}}),
    ...(outputDataReferences == null
      ? {}
      : {stepOutputChildren: {[SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: outputDataReferences}}),
  });
}

export function getRetypeColumnOptions(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep,
  inputDataReference: DataReference,
  inputDataFrameId?: string,
  outputDataReferences?: Array<DataReference>
): DataFrameRetypeColumnOptions {
  const inputStep = getStepFromAnalysis(analysis, inputDataReference.stepId);
  inputDataFrameId =
    inputDataFrameId ?? findAndAssertSingleStepDataFrameId(inputStep, inputDataReference.stepDataName);

  if (step.definition.stepType === PipelineStepType.RETYPE_COLUMNS) {
    const retypeColumn = async (
      columnSystemName: string,
      newColumnType?: RetypeColumnType
    ): Promise<void> => {
      await withLoadingScreen(
        updateRetypeColumnStep(analysis, step, columnSystemName, newColumnType),
        LOADING_MESSAGE
      );
    };
    return {retypeColumn};
  }
  // Previous step in chain is not a retype column step, so we make an new transform
  const retypeColumn = async (columnSystemName: string, newColumnType?: RetypeColumnType): Promise<void> => {
    await withLoadingScreen(
      addRetypeColumnStep(
        analysis,
        columnSystemName,
        newColumnType!,
        inputDataReference,
        inputDataFrameId,
        outputDataReferences
      ),
      LOADING_MESSAGE
    );
  };
  return {retypeColumn};
}
